/* istanbul ignore file */
import { navContentStore } from "@phn/state/nav-content-store";
import { Fragment, getAssetPath, h } from "@stencil/core";
import { getConfig, isDesktop } from "@phn/utility/helper";
import { GlobalDataLayerService } from "@phn/services/analytics/global-data-layer-service";
import {
  ClickElementTypes,
  ElementTypes,
  EventActions,
} from "@phn/utility/constants";
import { navStateStore } from "@phn/state/nav-state-store";
import { TMenu, TVehiclePurchaseMenu } from "@phn/entities/content";
import { getMenuContent } from "../../../services/menu-items-service";
import analyticsManager from "../../../services/analytics/analytics-manager";
import { getCurrentRoute } from "../../../services/routing-manager";

interface ElectricTypes {
  activeRoutingKey: string;
  topLevelRoutingKey: string;
  breakpoint: number;
  receivedContent: boolean;
}

export default function Electric({
  activeRoutingKey,
  topLevelRoutingKey,
  breakpoint,
  receivedContent,
}: ElectricTypes): HTMLElement {
  const showHeadline = !isDesktop(breakpoint);
  const displayedContent = getMenuContent(
    navContentStore.state,
    activeRoutingKey,
    topLevelRoutingKey,
  ) as TVehiclePurchaseMenu;

  /* istanbul ignore if */
  if (displayedContent === null) {
    return <div></div>;
  }

  const [tiles, firstItem, ...menuChildren] = displayedContent.children as [
    TVehiclePurchaseMenu,
    TMenu,
  ];

  const menu = structuredClone(displayedContent);
  menu.children = menuChildren;

  return (
    <Fragment>
      <TileComponent
        tiles={tiles}
        isMobile={showHeadline}
        firstItem={firstItem}
      />
      <phn-nd-menu
        item={menu}
        showHeadline={showHeadline}
        receivedContent={receivedContent}
        skipFirstChildCheck={true}
      />
    </Fragment>
  );
}

function TileComponent({
  tiles,
  isMobile,
  firstItem,
}: {
  tiles: TVehiclePurchaseMenu;
  isMobile: boolean;
  firstItem: TMenu;
}) {
  return (
    <div class="electric-ownership">
      <phn-p-link-pure
        stretch={!isMobile}
        align-label="start"
        href={firstItem.link}
        size="inherit"
        class="overview-link"
        onClick={() => trackModelTileClick(tiles[0])}
      >
        {firstItem.text}
      </phn-p-link-pure>

      {tiles.children.map((tile, index) => (
        <phn-p-link-tile
          aria-label={tile.text}
          href={tile.link}
          onClick={() => trackModelTileClick(tile)}
          description={tile.text}
          key={tile.id}
          compact={true}
          size="medium"
          aspect-ratio={index === 0 ? "4/3" : "16/9"}
          weight="regular"
          tabindex="0"
        >
          <img
            srcset={index === 0 ? getImg("electric") : getImg("charge")}
            alt={tile.text}
            width="477"
            height={index === 0 ? "358" : "268"}
          />
        </phn-p-link-tile>
      ))}
    </div>
  );
}

function trackModelTileClick(tile: TMenu): void {
  GlobalDataLayerService.getInstance().pushDataLayerEvent(
    EventActions.MODEL_CLICK,
    {
      componentClick: {
        clickElementId: tile.id,
        clickElementName: tile.text,
        clickElementType: ClickElementTypes.Link,
        targetUrl: tile.link,
      },
      componentDisplay: {
        displayElementId: analyticsManager.calculateDisplayElementId(),
        displayElementType: analyticsManager.calculateDisplayElementType(
          getCurrentRoute(),
          ElementTypes.bodyTypeButton,
        ),
        displayElementName: tile.text,
      },
    },
  );
}

function getImg(img: string): string {
  const assetsUrl = getConfig(navStateStore.state.env).ASSETS_URL;
  const assetPath = getAssetPath(`${assetsUrl}/series-assets/all`);
  return `${assetPath}/${img}.webp, ${assetPath}/${img}@2x.webp 2x`;
}
